import React from "react";
import styles from "./IdvStatusBadge.module.scss";
import CheckGreenCircleIcon from "../../assets/svg/icons/common/check-green-circle";
import CrossRedCircleIcon from "../../assets/svg/icons/common/cross-red-circle";
import PendingIcon from "../../assets/svg/icons/common/PendingIcon";

// Mirrors PowerappPlaidIdv::OUTCOME_*
export const IDV_PASSED = "passed";
export const IDV_FAILED = "failed";
export const IDV_PENDING_REVIEW = "pending_review";

const IDENTITY_FIELDS = ["first_name", "last_name", "email"];

const normalizeIdentityValue = (value) =>
  String(value ?? "").trim().toLowerCase();

/**
 * One string per person for a { first_name, last_name, email } record.
 * Normalized like PowerappPlaidIdv::ownerUniqueIdFor on the server.
 */
export const identityKey = (identity) =>
  IDENTITY_FIELDS.map((key) => normalizeIdentityValue(identity?.[key])).join("|");

export const isSameIdentity = (a, b) => identityKey(a) === identityKey(b);

/**
 * The completed powerapp_plaid_idv row for the owner with this identity, or
 * null. Verification follows the person rather than a form position or signer
 * row, so an owner whose name or email has changed no longer matches it.
 */
export const findIdvRow = (rows, identity) => {
  // An owner is only identifiable once their name and email are all filled in
  const complete = IDENTITY_FIELDS.every((key) =>
    normalizeIdentityValue(identity?.[key]),
  );
  if (!Array.isArray(rows) || !complete) {
    return null;
  }

  return (
    rows.find(
      (row) => row?.status === "completed" && isSameIdentity(row, identity),
    ) || null
  );
};

/**
 * Plaid IDV outcome for the owner with this identity, or null when they have
 * no completed session.
 */
export const getIdvOutcome = (rows, identity) => {
  const row = findIdvRow(rows, identity);

  // A completed session with no outcome yet is awaiting review, not passed.
  return row ? row.outcome || IDV_PENDING_REVIEW : null;
};

const VARIANTS = {
  [IDV_PASSED]: {
    className: styles.passed,
    icon: <CheckGreenCircleIcon width="14" height="14" />,
    label: "ID verified",
  },
  [IDV_PENDING_REVIEW]: {
    className: styles.pending,
    icon: <PendingIcon />,
    label: "ID under review",
  },
  [IDV_FAILED]: {
    className: styles.failed,
    icon: <CrossRedCircleIcon size={14} />,
    label: "ID verification failed",
  },
};

/**
 * `inline` is for placing the badge in running text (a block heading) rather
 * than in a flex row.
 */
function IdvStatusBadge({ outcome, inline = false }) {
  const variant = VARIANTS[outcome];
  if (!variant) {
    return null;
  }

  return (
    <span
      className={`${styles.badge} ${variant.className} ${inline ? styles.inline : ""}`}
      title="EMS pulls the Plaid report a few minutes after the session is submitted."
    >
      {variant.icon}
      {variant.label}
    </span>
  );
}

export default IdvStatusBadge;
